import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

interface GallerySectionProps {
  mode?: 'full' | 'preview';
}

export default function GallerySection({ mode = 'full' }: GallerySectionProps) {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [mobileSlideIndex, setMobileSlideIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);
  const autoPlayRef = useRef<NodeJS.Timeout | null>(null);

  const galleryImages = [
    // Column 1 - 3 images (new image added at top)
    {
      src: 'https://static.readdy.ai/image/36990eb3aa57e3c7d396ac7e72f77043/b183d74a6e7f3c7f390a50a74d104610.jpeg',
      alt: 'Art therapy creative expression and healing artwork'
    },
    {
      src: 'https://i.ibb.co/SXQbtswb/IMG-4546.jpg',
      alt: 'Two women engaged in collaborative art therapy session'
    },
    {
      src: 'https://i.ibb.co/Y7rqDqzc/IMG-5193.jpg',
      alt: 'Intricate mandala artwork with geometric patterns and vibrant colors'
    },
    // Column 2 - 2 images
    {
      src: 'https://i.ibb.co/YFmVr4Qt/IMG-5216.jpg',
      alt: 'Art therapy session with colorful paintings and creative materials'
    },
    {
      src: 'https://i.ibb.co/ZyP0Xzz/IMG-4551.jpg',
      alt: 'Art therapy workspace with brushes and creative supplies'
    },
    // Column 3 - 2 images
    {
      src: 'https://i.ibb.co/84Q9NMMR/IMG-5214.jpg',
      alt: 'Abstract neurographic drawing with flowing organic lines'
    },
    {
      src: 'https://i.ibb.co/sp0RxMSD/IMG-5195.jpg',
      alt: 'Intricate mandala pattern drawing with geometric designs'
    }
  ];

  const previewImages = [
    {
      src: 'https://storage.helloreaddy.io/project_files/b4f3eeda-f893-4987-8921-4636c63c4dc6/483ebc74-33c0-49d6-8403-0fb7b22e7bb7_compressed_IMG_4546.webp',
      alt: 'Close-up hands drawing stained-glass pattern in art therapy session'
    },
    {
      src: 'https://storage.helloreaddy.io/project_files/b4f3eeda-f893-4987-8921-4636c63c4dc6/406bce81-31d6-4c17-aa81-61bfe6112696_compressed_IMG_5192.webp',
      alt: 'Group art therapy session at the table with child and multiple participants'
    },
    {
      src: 'https://storage.helloreaddy.io/project_files/b4f3eeda-f893-4987-8921-4636c63c4dc6/fd734744-98f4-46c9-ae60-d84726475786_compressed_IMG_5198.webp',
      alt: 'Two women smiling and holding up their finished tree drawings'
    }
  ];

  const images = mode === 'preview' ? previewImages : galleryImages;

  // Mobile slider images - different from desktop gallery
  const mobileSliderImages = [
    {
      src: 'https://i.ibb.co/cc41bpSZ/IMG-4546.jpg',
      alt: 'Art therapy creative expression and healing artwork'
    },
    {
      src: 'https://i.ibb.co/CKxNZ31V/IMG-5191.jpg',
      alt: 'Collaborative art therapy session'
    },
    {
      src: 'https://i.ibb.co/R4TN2cgk/IMG-5193.jpg',
      alt: 'Intricate mandala artwork with geometric patterns'
    },
    {
      src: 'https://i.ibb.co/84Q9NMMR/IMG-5214.jpg',
      alt: 'Abstract neurographic drawing with flowing lines'
    },
    {
      src: 'https://i.ibb.co/v4xB3btG/IMG-5215.jpg',
      alt: 'Art therapy creative process'
    },
    {
      src: 'https://i.ibb.co/YFmVr4Qt/IMG-5216.jpg',
      alt: 'Art therapy session with colorful paintings'
    }
  ];

  // Mobile slider autoplay
  useEffect(() => {
    if (mode !== 'full' || !isAutoPlaying) return;

    autoPlayRef.current = setInterval(() => {
      setMobileSlideIndex((prev) => (prev + 1) % mobileSliderImages.length);
    }, 2000); // Change slide every 2 seconds

    return () => {
      if (autoPlayRef.current) {
        clearInterval(autoPlayRef.current);
      }
    };
  }, [isAutoPlaying, mobileSliderImages.length]);

  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStart(e.targetTouches[0].clientX);
    setIsAutoPlaying(false);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (touchStart - touchEnd > 75) {
      // Swipe left
      setMobileSlideIndex((prev) => (prev + 1) % mobileSliderImages.length);
    }

    if (touchStart - touchEnd < -75) {
      // Swipe right
      setMobileSlideIndex((prev) => (prev - 1 + mobileSliderImages.length) % mobileSliderImages.length);
    }

    // Resume autoplay after 3 seconds
    setTimeout(() => {
      setIsAutoPlaying(true);
    }, 3000);
  };

  const goToSlide = (index: number) => {
    setMobileSlideIndex(index);
    setIsAutoPlaying(false);
    setTimeout(() => {
      setIsAutoPlaying(true);
    }, 3000);
  };

  const nextSlide = () => {
    setMobileSlideIndex((prev) => (prev + 1) % mobileSliderImages.length);
    setIsAutoPlaying(false);
    setTimeout(() => {
      setIsAutoPlaying(true);
    }, 3000);
  };

  const prevSlide = () => {
    setMobileSlideIndex((prev) => (prev - 1 + mobileSliderImages.length) % mobileSliderImages.length);
    setIsAutoPlaying(false);
    setTimeout(() => {
      setIsAutoPlaying(true);
    }, 3000);
  };

  const openLightbox = (index: number) => {
    setCurrentImageIndex(index);
    setLightboxOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    document.body.style.overflow = 'unset';
  };

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % images.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') closeLightbox();
    if (e.key === 'ArrowRight') nextImage();
    if (e.key === 'ArrowLeft') prevImage();
  };

  return (
    <>
      <section id="gallery" className="pt-8 pb-8 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <div className="text-center mb-4">
            <h2 
              className="text-4xl lg:text-5xl font-normal cinematic-text-dark mb-4"
            >
              Creative Process & Artworks
            </h2>
            <p 
              className="text-lg sm:text-xl text-[#312921]/85 max-w-3xl mx-auto px-4"
            >
              Art becomes a safe space to express and transform
            </p>
          </div>

          {mode === 'full' && (
            <>
              {/* Mobile Slider - Only visible on mobile */}
              <div className="lg:hidden relative w-full overflow-hidden">
                <div 
                  className="relative w-full"
                  style={{ height: '500px' }}
                  onTouchStart={handleTouchStart}
                  onTouchMove={handleTouchMove}
                  onTouchEnd={handleTouchEnd}
                >
                  {mobileSliderImages.map((image, index) => (
                    <div 
                      key={index}
                      className="absolute top-0 left-0 w-full h-full transition-all duration-500 ease-out cursor-pointer"
                      style={{ 
                        transform: `translateX(${(index - mobileSlideIndex) * 100}%)`,
                        opacity: index === mobileSlideIndex ? 1 : 0.3
                      }}
                      onClick={() => openLightbox(index)}
                    >
                      <img
                        src={image.src}
                        alt={image.alt}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    </div>
                  ))}

                  {/* Navigation Arrows */}
                  <button
                    onClick={prevSlide}
                    className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center bg-white/80 hover:bg-white rounded-full shadow-lg transition-all duration-300 cursor-pointer z-10"
                    aria-label="Previous slide"
                  >
                    <i className="ri-arrow-left-s-line text-xl text-[#312921]"></i>
                  </button>
                  
                  <button
                    onClick={nextSlide}
                    className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center bg-white/80 hover:bg-white rounded-full shadow-lg transition-all duration-300 cursor-pointer z-10"
                    aria-label="Next slide"
                  >
                    <i className="ri-arrow-right-s-line text-xl text-[#312921]"></i>
                  </button>
                </div>

                {/* Dots Navigation */}
                <div className="flex justify-center gap-2 mt-8 mb-0">
                  {mobileSliderImages.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => goToSlide(index)}
                      className={`w-2 h-2 rounded-full transition-all duration-300 cursor-pointer ${
                        index === mobileSlideIndex 
                          ? 'bg-[#3D5C4A] w-8' 
                          : 'bg-gray-400 hover:bg-gray-600'
                      }`}
                      aria-label={`Go to slide ${index + 1}`}
                    />
                  ))}
                </div>
              </div>

              {/* Desktop Masonry Grid - Only visible on desktop */}
              <div className="hidden lg:grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5 lg:gap-x-6 lg:gap-y-3">
                {/* Column 1 - 3 images */}
                <div className="flex flex-col gap-4 md:gap-5 lg:gap-3">
                  {[0, 1, 2].map((index) => (
                    <div 
                      key={index}
                      className="relative overflow-hidden rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 cursor-pointer group flex-1"
                      onClick={() => openLightbox(index)}
                    >
                      <img
                        src={galleryImages[index].src}
                        alt={galleryImages[index].alt}
                        className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                        loading="lazy"
                      />
                    </div>
                  ))}
                </div>

                {/* Column 2 - 2 images */}
                <div className="flex flex-col gap-4 md:gap-5 lg:gap-3">
                  {[3, 4].map((index) => (
                    <div 
                      key={index}
                      className="relative overflow-hidden rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 cursor-pointer group flex-1"
                      onClick={() => openLightbox(index)}
                    >
                      <img
                        src={galleryImages[index].src}
                        alt={galleryImages[index].alt}
                        className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                        loading="lazy"
                      />
                    </div>
                  ))}
                </div>

                {/* Column 3 - 2 images */}
                <div className="flex flex-col gap-4 md:gap-5 lg:gap-3">
                  {[5, 6].map((index) => (
                    <div 
                      key={index}
                      className="relative overflow-hidden rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 cursor-pointer group flex-1"
                      onClick={() => openLightbox(index)}
                    >
                      <img
                        src={galleryImages[index].src}
                        alt={galleryImages[index].alt}
                        className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                        loading="lazy"
                      />
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}

          {mode === 'preview' && (
            <>
              {/* Desktop Preview Grid */}
              <div className="hidden lg:grid grid-cols-3 gap-4 md:gap-5 lg:gap-x-6 lg:gap-y-3">
                {previewImages.map((image, index) => (
                  <div 
                    key={index}
                    className="relative overflow-hidden rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 cursor-pointer group"
                  >
                    <img
                      src={image.src}
                      alt={image.alt}
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                      loading="lazy"
                    />
                  </div>
                ))}
              </div>

              {/* Mobile Preview Grid */}
              <div className="lg:hidden grid grid-cols-1 gap-4">
                {previewImages.map((image, index) => (
                  <div 
                    key={index}
                    className="relative overflow-hidden rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 cursor-pointer group"
                  >
                    <img
                      src={image.src}
                      alt={image.alt}
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                      loading="lazy"
                    />
                  </div>
                ))}
              </div>

              <div className="flex justify-center mt-6">
                <Link
                  to="/gallery"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-[#81998C] text-white rounded-full hover:bg-[#385B47] transition-colors duration-300 text-base font-normal whitespace-nowrap"
                >
                  See all artworks
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            </>
          )}
        </div>
      </section>

      {/* Lightbox */}
      {lightboxOpen && (
        <div 
          className="fixed inset-0 bg-[#385B47]/90 z-50 flex items-center justify-center p-4"
          onClick={closeLightbox}
          onKeyDown={handleKeyDown}
          tabIndex={0}
          role="dialog"
          aria-modal="true"
          aria-label="Image gallery lightbox"
        >
          <button
            onClick={closeLightbox}
            className="absolute top-4 right-4 cinematic-text-light hover:text-white transition-colors z-10"
            aria-label="Close lightbox"
          >
            <i className="ri-close-line text-3xl"></i>
          </button>
          
          <button
            onClick={(e) => { e.stopPropagation(); prevImage(); }}
            className="absolute left-4 top-1/2 -translate-y-1/2 cinematic-text-light hover:text-white transition-colors z-10"
            aria-label="Previous image"
          >
            <i className="ri-arrow-left-line text-3xl"></i>
          </button>
          
          <button
            onClick={(e) => { e.stopPropagation(); nextImage(); }}
            className="absolute right-4 top-1/2 -translate-y-1/2 cinematic-text-light hover:text-white transition-colors z-10"
            aria-label="Next image"
          >
            <i className="ri-arrow-right-line text-3xl"></i>
          </button>
          
          <div className="max-w-4xl max-h-full flex items-center justify-center">
            <img
              src={images[currentImageIndex].src}
              alt={images[currentImageIndex].alt}
              className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
          
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white text-center">
            <p className="text-sm cinematic-text-light-secondary">
              {currentImageIndex + 1} of {images.length}
            </p>
            <p className="text-xs cinematic-text-light-secondary mt-1">
              {images[currentImageIndex].alt}
            </p>
          </div>
        </div>
      )}
    </>
  );
}